<template>
  <IntercomModal
    v-model:show="showModal"
    :title="customer ? $t('customers.editCustomer') : $t('customers.addCustomer')"
    width="600px"
  >
    <div class="customer-form">
      <IntercomInput
        v-model="formData.name"
        :label="$t('customers.name')"
        :placeholder="$t('customers.name')"
        required
        :error="errors.name"
      />

      <IntercomPhoneInput
        v-model="formData.phone"
        :label="$t('customers.phone')"
        :placeholder="$t('customers.phone')"
        required
        :error="errors.phone"
        @update:valid="(v) => (phoneValid = v)"
      />

      <IntercomEmailInput
        v-model="formData.email"
        :label="$t('customers.email')"
        :placeholder="$t('customers.email')"
        :error="errors.email"
      />

      <IntercomInput
        v-model="formData.address"
        type="textarea"
        :label="$t('customers.address')"
        :placeholder="$t('customers.address')"
        :rows="3"
      />

      <div class="form-row">
        <IntercomInput
          v-model="formData.gstin"
          :label="$t('customers.gstin')"
          placeholder="e.g. 33AABCT1234F1ZP"
          maxlength="15"
        />
        <IntercomInput
          v-model="formData.state_code"
          :label="$t('customers.stateCode')"
          placeholder="e.g. 33"
          maxlength="2"
        />
      </div>

      <div class="form-row">
        <IntercomInput
          v-model="formData.credit_limit"
          type="number"
          label="Credit Limit (Khata)"
          placeholder="Leave blank for unlimited"
          :min="0"
          hint="Maximum credit allowed for this customer"
        />
        <IntercomInput
          v-if="!customer"
          v-model="formData.opening_balance"
          type="number"
          label="Opening Balance"
          placeholder="0.00"
          :min="0"
          hint="Existing balance to migrate"
        />
      </div>
    </div>

    <template #footer>
      <IntercomButton variant="secondary" @click="handleCancel">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton variant="primary" :loading="isLoading" @click="handleSubmit">
        {{ $t('common.save') }}
      </IntercomButton>
    </template>
  </IntercomModal>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { IntercomModal, IntercomInput, IntercomPhoneInput, IntercomEmailInput, IntercomButton } from '@shared/components/intercom'
import { useBilling } from '../composables/useBilling.js'

const props = defineProps({
  show: Boolean,
  customer: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['update:show', 'success'])

const { t } = useI18n()
const { createCustomer, updateCustomer } = useBilling()

// State
const showModal = ref(props.show)
const isLoading = ref(false)
const phoneValid = ref(false)

// Form data
const formData = reactive({
  name: '',
  phone: '',
  email: '',
  address: '',
  gstin: '',
  state_code: '',
  credit_limit: '',
  opening_balance: '',
})

// Form errors
const errors = reactive({
  name: '',
  phone: '',
  email: '',
})

// Watch show prop
watch(
  () => props.show,
  (val) => {
    showModal.value = val
    if (val && props.customer) {
      // Edit mode - populate form
      Object.assign(formData, {
        name: props.customer.name,
        phone: props.customer.phone,
        email: props.customer.email || '',
        address: props.customer.address || '',
        gstin: props.customer.gstin || '',
        state_code: props.customer.state_code || '',
        credit_limit: props.customer.credit_limit != null ? String(props.customer.credit_limit) : '',
        opening_balance: '',
      })
    } else {
      // Create mode - reset form
      resetForm()
    }
    // Clear errors when opening
    clearErrors()
  }
)

// Watch modal state
watch(showModal, (val) => {
  emit('update:show', val)
})

/**
 * Clear errors
 */
const clearErrors = () => {
  errors.name = ''
  errors.phone = ''
  errors.email = ''
}

/**
 * Validate form
 */
const validateForm = () => {
  clearErrors()
  let isValid = true

  // Validate name
  if (!formData.name || formData.name.trim() === '') {
    errors.name = 'Customer name is required'
    isValid = false
  }

  // Validate phone (must be 10 digits)
  if (!formData.phone || formData.phone.trim() === '') {
    errors.phone = 'Phone is required'
    isValid = false
  } else if (!phoneValid.value) {
    errors.phone = 'Phone must be exactly 10 digits'
    isValid = false
  }

  // Validate email format (if provided)
  if (formData.email && formData.email.trim() !== '') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      errors.email = 'Invalid email format'
      isValid = false
    }
  }

  return isValid
}

/**
 * Reset form
 */
const resetForm = () => {
  Object.assign(formData, {
    name: '',
    phone: '',
    email: '',
    address: '',
    gstin: '',
    state_code: '',
    credit_limit: '',
    opening_balance: '',
  })
  clearErrors()
}

/**
 * Handle cancel
 */
const handleCancel = () => {
  showModal.value = false
  resetForm()
}

/**
 * Handle submit
 */
const handleSubmit = async () => {
  // Validate form
  if (!validateForm()) {
    return
  }

  isLoading.value = true

  try {
    // Strip +91 prefix — backend expects 10-digit phone
    let phone = formData.phone
    if (phone && phone.startsWith('+91')) phone = phone.slice(3)

    // Prepare data
    const customerData = {
      name: formData.name,
      phone: phone,
      email: formData.email || null,
      address: formData.address || null,
      gstin: formData.gstin || null,
      state_code: formData.state_code || null,
      credit_limit: formData.credit_limit !== '' ? parseFloat(formData.credit_limit) : null,
      opening_balance: formData.opening_balance !== '' ? parseFloat(formData.opening_balance) : null,
    }

    // Create or update
    if (props.customer) {
      await updateCustomer(props.customer.id, customerData)
      console.log(t('customers.updateSuccess'))
    } else {
      await createCustomer(customerData)
      console.log(t('customers.createSuccess'))
    }

    // Close modal and emit success
    showModal.value = false
    resetForm()
    emit('success')
  } catch (error) {
    console.error('Failed to save customer:', error)
    // TODO: Show error notification
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.customer-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

@media (max-width: 480px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
